<template>
<v-container v-if="!$store.state.mobile">
    <v-card class="mt-5">
        <div>
            <v-row class="primary-header pa-3 ma-0" align="center">
                <span style="font-size: 25px">{{ title }}</span>
            </v-row>
            <v-col>
                <div class="mb-3" style="font-size: 18px">{{ description }}</div>
                <div class="mb-3" style="font-size: 15px" v-for="text in texts" :key="text">
                    {{ text }}
                </div>
            </v-col>
        </div>

        <v-divider />

        <v-col class="pa-4">
            <v-row class="my-4" justify="center">
                <v-btn color="primary" @click="onNewBtnClicked">
                    새 게임
                    <v-icon>{{ newGameIcon }}</v-icon>
                </v-btn>
            </v-row>

            <v-card class="pa-3" v-if="isNewGame">
                <v-form ref="startForm">
                    <v-row>
                        <v-col cols="2" class="category">
                            파일명
                        </v-col>
                        <v-col class="py-0">
                            <v-text-field v-model="name" solo :rules="nameRules" style="max-width: 300px" />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="2" class="category">시작일</v-col>
                        <v-col>
                            <v-menu v-model="dateMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" min-width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="startDate" solo style="max-width: 300px" label="시작 날짜를 선택하세요" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" :rules="startDateRules" />
                                </template>
                                <v-date-picker class="ma-0" v-model="startDate" type="month" locale="ko-kr" @input="dateMenu = false" />
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="2" class="category">
                            시작금
                        </v-col>
                        <v-col class="py-1">
                            <v-text-field v-model="cash" suffix="원" solo @keypress="checkNumber" @input="onInput" :rules="cashRule" style="max-width: 300px" />
                        </v-col>
                    </v-row>
                    <v-row justify="end">
                        <v-btn color="primary" @click="playGame">시작하기</v-btn>
                    </v-row>
                </v-form>
            </v-card>

            <v-row class="my-4" justify="center">
                <v-btn color="primary" @click="onLoadBtnClicked" :loading="isLoading">
                    이어하기
                    <v-icon>{{ loadGameIcon }}</v-icon>
                </v-btn>
            </v-row>

            <v-card class="pa-3" v-if="isLoadGame">
                <v-list rounded v-if="savedGames.length > 0">
                    <v-list-item v-for="game in savedGames" :key="game.id" style="cursor: pointer" @click="$router.push(`/simulation/${game.id}`)">
                        <v-row align="center">
                            <v-col>
                                <div class="game-header">이름</div>
                                <div>{{ game.name }}</div>
                            </v-col>
                            <v-col>
                                <div class="game-header">기간</div>
                                <div>{{ game.startDate }} - {{ game.date}}</div>
                            </v-col>
                            <v-col>
                                <div class="game-header">시작금</div>
                                <div>{{ commaFormat(game.startCash) }} 원</div>
                            </v-col>
                            <v-col>
                                <div class="game-header">총 자산</div>
                                <div>{{ commaFormat(totalAsset(game).toFixed(0)) }} 원</div>
                            </v-col>
                            <v-col>
                                <div class="game-header">총 손익률</div>
                                <div>{{ totalProfitRatio(totalAsset(game), game.startCash) }} %</div>
                            </v-col>
                            <v-col>
                                <div class="game-header">마지막 저장</div>
                                <div>{{ (new Date(game.lastSavedTime)).toLocaleString() }}</div>
                            </v-col>
                            <v-col>
                                <v-btn text @click.stop="deleteGame(game)">
                                    <v-icon>delete</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-list-item>
                </v-list>
                <div class="text-center" v-else>
                    저장된 게임이 없습니다
                </div>
            </v-card>
        </v-col>
    </v-card>
</v-container>

<v-container v-else>
    <v-card class="mt-5">
        <div>
            <v-row class="primary-header pa-3 ma-0" align="center">
                <span style="font-size: 20px">{{ title }}</span>
            </v-row>
            <v-col>
                <div class="mb-3" style="font-size: 15px">{{ description }}</div>
                <div class="mb-3" style="font-size: 12px" v-for="text in texts" :key="text">
                    {{ text }}
                </div>
            </v-col>
        </div>

        <v-divider />

        <v-col class="pa-4">
            <v-row class="my-4" justify="center">
                <v-btn color="primary" @click="onNewBtnClicked">
                    새 게임
                    <v-icon>{{ newGameIcon }}</v-icon>
                </v-btn>
            </v-row>

            <v-card class="pa-3" v-if="isNewGame">
                <v-form ref="startForm">
                    <v-text-field v-model="name" outlined label="파일명" :rules="nameRules" style="max-width: 300px" />

                    <v-menu v-model="dateMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="startDate" outlined style="max-width: 300px" label="시작 날짜를 선택하세요" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" :rules="startDateRules" />
                        </template>
                        <v-date-picker class="ma-0" v-model="startDate" type="month" locale="ko-kr" @input="dateMenu = false" />
                    </v-menu>

                    <v-text-field v-model="cash" suffix="원" outlined label="시작금" @keypress="checkNumber" @input="onInput" :rules="cashRule" style="max-width: 300px" />

                    <v-row justify="center">
                        <v-btn color="primary" @click="playGame">시작하기</v-btn>
                    </v-row>
                </v-form>
            </v-card>

            <v-row class="my-3" justify="center">
                <v-btn color="primary" @click="onLoadBtnClicked" :loading="isLoading">
                    이어하기
                    <v-icon>{{ loadGameIcon }}</v-icon>
                </v-btn>
            </v-row>

            <v-card class="px-1 py-3" v-if="isLoadGame">
                <div rounded v-if="savedGames.length > 0">
                    <v-row class="ma-0 my-2" v-for="game in savedGames" :key="game.id" style="cursor: pointer; border-radius: 15px; border: 2px solid #f0f0f0;" @click="$router.push(`/simulation/${game.id}`)" align="center">
                        <v-col class="game-header">
                            <div>·이름: {{ game.name }}</div>
                            <div>·기간: {{ game.startDate }} - {{ game.date}}</div>
                            <div>·시작금: {{ commaFormat(game.startCash) }} 원</div>
                            <div>·총 자산: {{ commaFormat(totalAsset(game).toFixed(0)) }} 원</div>
                            <div>·총 손익률: {{ totalProfitRatio(totalAsset(game), game.startCash) }} %</div>
                            <div>·마지막 저장: {{ (new Date(game.lastSavedTime)).toLocaleString() }}</div>
                        </v-col>
                        <v-col cols="2">
                            <v-btn icon @click.stop="deleteGame(game)">
                                <v-icon color="primary">delete</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                </div>
                <div class="text-center" v-else>
                    저장된 게임이 없습니다
                </div>
            </v-card>
        </v-col>
    </v-card>
</v-container>
</template>

<script>
import firebase from 'firebase/app'
import {
    db,
    auth,
    functions
} from '@/firebase'
import {
    AccountToast
} from '@/mixins/account-toast.js'
import { maxDate } from '@/constants'

const moment = require("moment")

export default {
    metaInfo: {
        title: "Invest Helper | 모의투자",
        htmlAttrs: {
            lang: "ko-KR"
        },
        meta: [{
                charset: "utf-8"
            },
            {
                name: "description",
                content: "20년치 코스피/코스닥 주식 모의 투자를 통해 중장기 투자 실력을 테스트합니다"
            },
            {
                name: "keywords",
                content: "코스피, 코스닥, 모의투자"
            },
        ]
    },
    mixins: [
        AccountToast
    ],
    data: () => ({
        title: '국내주식 모의투자',
        description: '국내주식 (코스피, 코스닥)을 월단위로 모의투자하여 중장기 투자 전략 및 감각을 검증합니다',
        texts: [
            '1. 주가는 매월 말일 종가 기준',
            '2. 모든 종목의 사업보고서는 결산월에 관계없이 3월에 업데이트됨',
            '3. 모든 행위는 자동저장됩니다',
        ],
        savedGames: [],
        isNewGame: false,
        isLoadGame: false,
        dateMenu: false,
        name: '',
        nameRules: [
            v => {
                if (!v) return '이름을 입력해주세요'
                return true
            },
        ],
        startDate: "2001-01",
        startDateRules: [
            v => {
                if (!v) return '시작일을 입력해주세요'
                const year = parseInt(v.substring(0, 4))
                const month = parseInt(v.substring(5, 7))
                const today = moment(`${year}-${month}-01`)

                if (year < 2001) return '2001년 이후만 가능합니다'
                else if (today.isSameOrAfter(maxDate)) return `${maxDate.year()}년 ${maxDate.month() + 1}월 이전만 가능합니다`
                return true
            },
        ],
        cash: '1,000,000',
        cashRule: [
            v => {
                if (!v) return "시작금은 100만원 이상, 100억원 이하여야 합니다"

                const cash = parseInt(v.split(",").join(""))
                if (cash < 1000000 || cash > 10000000000) return "시작금은 100만원 이상, 100억원 이하여야 합니다"

                return true
            }
        ],
        isLoading: false
    }),
    computed: {
        user() {
            return this.$store.getters.user
        },
        newGameIcon() {
            return this.isNewGame ? "keyboard_arrow_up" : "keyboard_arrow_down"
        },
        loadGameIcon() {
            return this.isLoadGame ? "keyboard_arrow_up" : "keyboard_arrow_down"
        },
    },
    created() {},
    async mounted() {},
    methods: {
        async playGame() {
            if (!this.$refs.startForm.validate()) return

            if (this.savedGames.length >= 3) {
                this.onLoadBtnClicked()
                return this.$toasted.error('최대 3개의 게임만 진행할 수 있습니다')
            }

            const docRef = await db.collection("users").doc(this.user.id).collection("simulations").add({
                name: this.name,
                lastSavedTime: Date.now(),
                startDate: this.startDate,
                date: this.startDate,
                startCash: parseInt(this.cash.split(",").join("")),
                cash: parseInt(this.cash.split(",").join("")),
                stocks: {}
            })

            this.$router.push(`/simulation/${docRef.id}`)
        },
        onNewBtnClicked() {
            if (!this.$store.getters.isLoggedIn) return this.showLoginToast()

            if (!this.$store.getters.checkActive) return this.showVerifyToast()

            if (this.savedGames.length >= 3) {
                this.onLoadBtnClicked()
                return this.$toasted.error('최대 3개의 게임만 진행할 수 있습니다')
            }

            this.isNewGame = true
            this.isLoadGame = false
        },
        async onLoadBtnClicked() {
            if (!this.$store.getters.isLoggedIn) return this.showLoginToast()

            if (!this.$store.getters.checkActive) return this.showVerifyToast()

            this.isLoading = true
            this.savedGames = await db.collection('users').doc(this.user.id).collection('simulations').parseCollection()
            this.isLoading = false

            this.isLoadGame = true
            this.isNewGame = false
        },
        checkNumber($event) {
            const number = $event.key.replace(/[^0-9]/g, '')
            if (!number) {
                return $event.preventDefault()
            }
        },
        onInput(value) {
            const numbers = value.replace(/[^0-9]/g, '')
            this.cash = numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        },
        deleteGame(game) {
            if (!confirm('정말 삭제하시겠습니까?')) return

            const deleteFn = functions.httpsCallable('recursiveDelete')
            const path = db.collection('users').doc(this.user.id).collection('simulations').doc(game.id).path

            deleteFn({
                path,
                userId: this.user.id
            })

            const index = this.savedGames.indexOf(game)
            this.savedGames.splice(index, 1)
        },
        totalAsset(game) {
            let stockAsset = 0
            for (const code in game.stocks) stockAsset += game.stocks[code].total * 0.9975
            return game.cash + stockAsset
        },
        totalProfitRatio(total, start) {
            return ((total / start - 1) * 100).toFixed(2)
        },
        commaFormat(value) {
            if (!value) return 0
            return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        },
        textColor(value) {
            if (value > 0) return 'red'
            else if (value < 0) return 'blue'
            else return 'black'
        },
    },
}
</script>

<style>
.game-header {
    font-size: 6px;
    font-weight: bold;
}

.category {
    font-size: 16px;
    font-weight: bold;
}

.error-message {
    font-size: 12px;
    color: red;
}
</style>
